import "./App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { HomePage } from "./pages/content/home";
import { TrackParcelPage } from "./pages/content/track_parcel";
import AuthService from "./services/auth.service";
import PrivateRoute from "./authorization/private-route.component";
import BusinessRoute from "./authorization/business-route.component";
import { AboutPage } from "./pages/content/about";
import { FAQPage } from "./pages/content/faq";
import { HowItWorksPage } from "./pages/content/how-it-works";
import { ContactPage } from "./pages/content/contact";
import { TermsPage } from "./pages/content/terms";
import { PrivacyPage } from "./pages/content/privacy";
import { NotFoundPage } from "./pages/content/not-found";
import MainLayout from "./components/MainLayout";
import { PageLoader } from "./components/PageLoader";

const lazyNamed = (loadModule, exportName) =>
  lazy(() => loadModule().then((loadedModule) => ({ default: loadedModule[exportName] })));

const SignupPage = lazyNamed(() => import("./pages/auth/signup"), "SignupPage");
const LoginPage = lazyNamed(() => import("./pages/auth/login"), "LoginPage");
const AdminLoginPage = lazyNamed(() => import("./pages/auth/admin-login"), "AdminLoginPage");
const ResetRequestPage = lazyNamed(() => import("./pages/auth/request-reset-password"), "ResetRequestPage");
const ResetPasswordPage = lazyNamed(() => import("./pages/auth/reset-password"), "ResetPasswordPage");
const DevelopersPage = lazyNamed(() => import("./pages/content/developers"), "DevelopersPage");
const ParcelPage = lazyNamed(() => import("./pages/content/parcel"), "ParcelPage");
const ParcelsPage = lazyNamed(() => import("./pages/content/parcels"), "ParcelsPage");
const CartPage = lazyNamed(() => import("./pages/content/cart"), "CartPage");
const ProfilePage = lazyNamed(() => import("./pages/content/profile"), "ProfilePage");
const DriverDashBoard = lazyNamed(() => import("./pages/content/driver_dashboard"), "DriverDashBoard");
const PaymentSuccessPage = lazyNamed(() => import("./pages/content/payment_success"), "PaymentSuccessPage");
const BusinessLayout = lazy(() => import("./components/business/BusinessLayout"));
const NotBusinessPage = lazy(() => import("./pages/business/NotBusinessPage"));
const OrdersPage = lazy(() => import("./pages/business/OrdersPage"));
const CreateOrderPage = lazy(() => import("./pages/business/CreateOrderPage"));
const BulkUploadPage = lazy(() => import("./pages/business/BulkUploadPage"));
const TrackPage = lazy(() => import("./pages/business/TrackPage"));
const SettingsPage = lazy(() => import("./pages/business/SettingsPage"));

const ReferralRedirect = () => {
  const { code } = useParams();
  return <Navigate to={`/signup?referral_key=${code}`} replace />;
};

const TrackParcelRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "/track-parcel", search }} replace />;
};

function App() {
  if (AuthService.getCurrentUser() && !AuthService.isLogin()) {
    AuthService.logout();
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>

          <Route path="/ref/:code" element={<ReferralRedirect />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/track_parcel" element={<TrackParcelRedirect />} />

          {/* ── Main site (with site Header + Footer) ── */}
          <Route element={<MainLayout />}>
            <Route path="/"                              element={<HomePage />} />
            <Route path="/login"                         element={<LoginPage />} />
            <Route path="/admin-login"                   element={<AdminLoginPage />} />
            <Route path="/signup"                        element={<SignupPage />} />
            <Route path="/reset-password"                element={<ResetRequestPage />} />
            <Route path="/auth/reset-password/:reset_token" element={<ResetPasswordPage />} />
            <Route path="/about-us"                      element={<AboutPage />} />
            <Route path="/faq"                           element={<FAQPage />} />
            <Route path="/how-it-works"                  element={<HowItWorksPage />} />
            <Route path="/contact"                       element={<ContactPage />} />
            <Route path="/terms"                         element={<TermsPage />} />
            <Route path="/privacy"                       element={<PrivacyPage />} />
            <Route path="/developers"                    element={<DevelopersPage />} />
            <Route path="/track-parcel"                  element={<TrackParcelPage />} />
            <Route path="/cart"                          element={<CartPage />} />
            <Route path="/payment-success"               element={<PaymentSuccessPage />} />

            <Route element={<PrivateRoute />}>
              <Route path="/profile"                     element={<ProfilePage />} />
              <Route path="/dashboard"                   element={<DriverDashBoard />} />
              <Route path="/parcels"                     element={<ParcelsPage />} />
              <Route path="/parcel/:parcel_id"           element={<ParcelPage />} />
              <Route path="/not-a-business"              element={<NotBusinessPage />} />
            </Route>

            <Route path="*"                              element={<NotFoundPage />} />
          </Route>

          {/* ── Business dashboard (own shell, no main Header/Footer) ── */}
          <Route element={<BusinessRoute />}>
            <Route element={<BusinessLayout />}>
              <Route path="/business/dashboard"          element={<OrdersPage />} />
              <Route path="/business/dashboard/create"   element={<CreateOrderPage />} />
              <Route path="/business/dashboard/bulk"     element={<BulkUploadPage />} />
              <Route path="/business/dashboard/track"    element={<TrackPage />} />
              <Route path="/business/dashboard/settings" element={<SettingsPage />} />
            </Route>
          </Route>

        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
